/// <reference path="../../../../@types/@99designs/common/components/CommentField/index.d.ts" />
/// <reference path="../../../../@types/@99designs/common/components/ScrollIntoViewElement/index.d.ts" />

import React from 'react';
import { intlShape } from 'react-intl';

import { Avatar, Props as AvatarProps } from '@99designs/common/components/Avatar';
import { CommentField } from '@99designs/common/components/CommentField';
import { ScrollIntoViewElement } from '@99designs/common/components/ScrollIntoViewElement';
import { Design } from './../../../../contest/apps/design-details/models/design';
import { User } from './../../../../contest/apps/design-details/models/user';
import CircumventionWarning, {
    circumventionKeywordMatches,
} from './../../../../contest/apps/messages/components/CircumventionWarning';
import RatingSuggestions from './rating-suggestions';

interface CommentFormProps {
    activeUser: User;
    recipientUser: User;
    design?: Design;
    author?: AvatarProps['user'];
    canScrollIntoViewOnMount?: boolean;
    isVertical?: boolean;
    isAutoFocus?: boolean;
    hasCancelButton?: boolean;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    onSubmit?: (content: string, asSystem?: boolean) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    scrollingContainer: Element;
    hasUserCommented?: boolean;
}
interface CommentFormState {
    textValue: string;
    selectedSuggestion: string | null;
    isAutoFocus: boolean;
}

export default class CommentForm extends React.Component<CommentFormProps, CommentFormState> {
    static contextTypes = {
        intl: intlShape,
    };
    static defaultProps = {
        design: {},
        canScrollIntoViewOnMount: false,
        hasCancelButton: false,
        isAutoFocus: false,
        isVertical: false,
        onCancel() {},
        onClick() {},
        onSubmit() {},
        onKeyDown() {},
        onKeyUp() {},
        hasUserCommented: false,
    };

    private commentField: CommentField | null = null;

    constructor(props: CommentFormProps) {
        super(props);
        this.state = {
            textValue: '',
            selectedSuggestion: null,
            isAutoFocus: false,
        };

        this.onTextChange = this.onTextChange.bind(this);
        this.useSuggestedComment = this.useSuggestedComment.bind(this);
    }

    render() {
        const shouldCheckCircumvention =
            this.props.activeUser &&
            this.props.recipientUser &&
            !(this.props.activeUser.role === 'admin' || this.props.recipientUser.role === 'admin');
        const matches = shouldCheckCircumvention
            ? circumventionKeywordMatches(this.state.textValue)
            : [];
        const showCircumventionWarning = matches.length > 0;

        const submitButtonLabel = <span className="icon icon--sent-1" />;

        return (
            <ScrollIntoViewElement
                scrollingContainer={this.props.scrollingContainer}
                isEnabled={true}
                canScrollOnMount={this.props.canScrollIntoViewOnMount}
            >
                <div>
                    <div className="comment-submit" onClick={this.props.onClick}>
                        {this.props.author && (
                            <div className="comment-submit__avatar">
                                <Avatar
                                    user={this.props.author}
                                    variations={['block', 'extra-small']}
                                />
                            </div>
                        )}
                        <div className="comment-submit__form">
                            {showCircumventionWarning && (
                                <CircumventionWarning
                                    user={this.props.activeUser}
                                    message={this.state.textValue}
                                    triggers={matches}
                                    location="Contests Messages"
                                />
                            )}
                            <CommentField
                                ref={input => {
                                    this.commentField = input;
                                }}
                                placeholder={this.context.intl.formatMessage({
                                    id: 'comment_form_placeholder',
                                })}
                                layout={this.props.isVertical ? 'vertical' : 'horizontal'}
                                isAutoFocus={this.state.isAutoFocus}
                                submitButtonLabel={submitButtonLabel}
                                submitButtonTooltip={this.context.intl.formatMessage({
                                    id: 'send_message_tooltip',
                                })}
                                cancelButtonLabel={this.context.intl.formatMessage({
                                    id: 'comment_cancel',
                                })}
                                hasCancelButton={this.props.hasCancelButton}
                                onCancel={this.props.onCancel}
                                onSubmit={this.props.onSubmit}
                                onKeyDown={this.props.onKeyDown}
                                onKeyUp={this.props.onKeyUp}
                                onChange={this.onTextChange}
                            />
                        </div>
                    </div>
                    {this.areRatingSuggestionsVisible() && (
                        <RatingSuggestions
                            design={this.props.design}
                            onSuggestionSelected={this.useSuggestedComment}
                        />
                    )}
                </div>
            </ScrollIntoViewElement>
        );
    }

    useSuggestedComment(msg: string) {
        const suggestion = msg.replace('…', ' ');
        if (this.commentField) {
            this.commentField.setState({
                textValue: suggestion,
            });
        }

        this.setState(() => {
            return {
                isAutoFocus: true,
                selectedSuggestion: suggestion,
            };
        });
    }

    onTextChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const value = e.target.value;
        this.setState(() => {
            return {
                textValue: value,
            };
        });
    }

    areRatingSuggestionsVisible() {
        return (
            !this.props.hasUserCommented &&
            this.props.design &&
            this.props.design.permission.canViewCommentSuggestions &&
            (this.state.textValue === '' || this.state.textValue === this.state.selectedSuggestion)
        );
    }
}



// WEBPACK FOOTER //
// ./src/web/js/contest/apps/design-details/components/comment-form.tsx